import { Injectable, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FeatureFlagService } from 'feature-flags-lib';
import { ErrorSeverity, MessageNotificationService } from 'message-notification-service-lib';
import { Subscription } from 'rxjs';
import { StageSet, WorkflowInProgressComponent } from './workflow-in-progress.component';

export type TemplateLaunchStage = 'Launching' | 'Preparing';

/**
 * Holds the blurred "working on it" overlay across a template direct-link launch.
 */
@Injectable({ providedIn: 'root' })
export class TemplateLaunchOverlayService {
  static readonly TIMEOUT_MS = 20000;

  static readonly STAGES: StageSet = {
    Launching: { text: 'Opening your template', icon: 'autorenew' },
    Preparing: { text: 'Preparing the document', icon: 'build_circle' },
  };

  private dialogRef: MatDialogRef<WorkflowInProgressComponent> | undefined;
  private timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  private supersededSubscription: Subscription | undefined;

  constructor(
    private dialog: MatDialog,
    private ngZone: NgZone,
    private messageNotificationService: MessageNotificationService,
    private featureFlagService: FeatureFlagService,
  ) {}

  get isShowing(): boolean {
    return this.dialogRef !== undefined;
  }

  /**
   * The overlay exists only to cover a template direct-link launch, so with the flag
   * off every entry point stands down rather than each caller having to check.
   */
  private get isEnabled(): boolean {
    return this.featureFlagService.enabled(FeatureFlagService.FLAGS.templateDirectUrl)();
  }

  /** Raises the overlay. No-op if it is already up, so this is safe to call twice. */
  show(): void {
    if (!this.isEnabled) {
      return;
    }

    if (this.dialogRef) {
      return;
    }

    this.ngZone.run(() => {
      this.dialogRef = this.dialog.open(WorkflowInProgressComponent, {
        disableClose: true,
        data: {
          title: 'Opening your template',
          stages: TemplateLaunchOverlayService.STAGES,
        },
      });
      this.dialogRef.componentInstance.receiveStatus('Launching');
    });

    // Any other dialog opening means the launch has reached something the user can
    // see and interact with, so the overlay has done its job.
    this.supersededSubscription = this.dialog.afterOpened.subscribe((openedRef) => {
      if (openedRef !== this.dialogRef) {
        this.hide();
      }
    });

    this.timeoutHandle = setTimeout(
      () => this.failAndHide('An error occurred while opening the template. Please contact Kyloe Support if the error persists.'),
      TemplateLaunchOverlayService.TIMEOUT_MS,
    );
  }

  /** Moves the overlay to a later stage. No-op if it is not up. */
  stage(status: TemplateLaunchStage): void {
    if (!this.isEnabled) {
      return;
    }

    if (!this.dialogRef) {
      return;
    }
    this.ngZone.run(() => this.dialogRef?.componentInstance.receiveStatus(status));
  }

  /** Drops the overlay and hands the app back to the user. Safe to call when not showing. */
  hide(): void {
    if (!this.isEnabled) {
      return;
    }

    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = undefined;
    }

    if (this.supersededSubscription) {
      this.supersededSubscription.unsubscribe();
      this.supersededSubscription = undefined;
    }

    if (!this.dialogRef) {
      return;
    }

    const ref = this.dialogRef;
    // Cleared before closing so a hide() triggered from within a close handler
    // cannot re-enter.
    this.dialogRef = undefined;
    this.ngZone.run(() => ref.close());
  }

  /**
   * Drops the overlay and tells the user why. For the unrecoverable stops, where
   * leaving them on a bare template list with no explanation would be worse than
   * the delay we are covering.
   */
  failAndHide(message: string): void {
    if (!this.isEnabled) {
      return;
    }

    const wasShowing = this.isShowing;
    this.hide();
    if (wasShowing) {
      this.messageNotificationService.error(message, undefined, undefined, ErrorSeverity.CRITICAL);
    }
  }
}
